import { calculateBasicStatistics } from '../statistics/basicStats';
import type { AnalysisOptions, ResultCache } from '../../stores/systems/keisuke/dashboardStore';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, KeisukeAdapterError, ktLng } from '../keisukeAdapterI18n';
import { formatPaperDocument } from '../keisukePaperFormat';

export function buildBasicStatsPaperText(
  col: string,
  result: ReturnType<typeof calculateBasicStatistics>,
  lng = 'en'
): string {
  const methods = ktLng(lng, 'paper.basicStats.methods', { col, n: result.n });
  const results = ktLng(lng, 'paper.basicStats.results', {
    col,
    mean: result.mean.toFixed(2),
    sd: result.stdDev.toFixed(2),
    median: result.median.toFixed(2),
    q1: result.q1.toFixed(2),
    q3: result.q3.toFixed(2),
    min: result.min.toFixed(2),
    max: result.max.toFixed(2),
    ciLower: result.confidenceInterval.lower.toFixed(2),
    ciUpper: result.confidenceInterval.upper.toFixed(2),
  });
  return formatPaperDocument(lng, methods, results);
}

function buildBasicStatsPaperTextLegacy(
  col: string,
  result: ReturnType<typeof calculateBasicStatistics>
): string {
  return buildBasicStatsPaperText(col, result, 'en');
}

export function runBasicStatsAdapter(
  data: DataRow[],
  options: AnalysisOptions,
  gridHeaders: string[]
): ResultCache {
  const mapping = options.mapping ?? {};
  let col = mapping.valueColumn;
  if (!col || !gridHeaders.includes(col)) {
    const numericCol = gridHeaders.find((h) =>
      data.some((row) => parseFiniteNumberFromCell(row[h]) !== null)
    );
    if (!numericCol) throw new KeisukeAdapterError('numericColumnNotFound');
    col = numericCol;
  }
  const values = data
    .map((row) => parseFiniteNumberFromCell(row[col!]))
    .filter((v): v is number => v !== null);
  if (values.length === 0) {
    throw new KeisukeAdapterError('invalidNumericData');
  }
  const result = calculateBasicStatistics(values);
  const stats: Record<string, unknown> = {
    column: col,
    n: result.n,
    mean: result.mean,
    median: result.median,
    stdDev: result.stdDev,
    stdError: result.stdError,
    min: result.min,
    max: result.max,
    q1: result.q1,
    q3: result.q3,
    ciLower: result.confidenceInterval.lower,
    ciUpper: result.confidenceInterval.upper,
  };
  return {
    summary: kt('summary.basicStats', { n: result.n }),
    stats,
    statsDetailText: kt('statsDetail.basicStats.line', {
      col,
      mean: result.mean.toFixed(4),
      sd: result.stdDev.toFixed(4),
      se: result.stdError.toFixed(4),
    }),
    paperText: buildBasicStatsPaperTextLegacy(col!, result),
    updatedAt: Date.now(),
    rawResult: result,
  };
}
